import { Component } from '../Component';

/**
 * Element types in the game
 */
export type ElementType =
  | 'solar'   // Sun/light/fire
  | 'verdant' // Plant/nature
  | 'aqua'    // Water/ice
  | 'aero'    // Wind/electric
  | 'terra'   // Earth/rock
  | 'void'    // Dark/shadow
  | 'tech'    // Machine/construct
  | 'spirit'; // Ghost/psychic

/**
 * Personality types affect AI behavior
 */
export type PersonalityType =
  | 'aggressive'  // Prefers attacking
  | 'defensive'   // Prefers protecting
  | 'supportive'  // Prefers helping allies
  | 'independent' // Acts more autonomously
  | 'cautious'    // Avoids risky situations
  | 'bold';       // Takes more risks

/**
 * BeastieComponent - Core beastie data
 * Represents a creature's identity and progression
 */
export class BeastieComponent extends Component {
  public readonly type = 'beastie';

  /** Species identifier (references static species data) */
  public speciesId: string;

  /** Custom name given by player */
  public nickname: string;

  /** Current level */
  public level: number;

  /** Experience points */
  public experience: number;

  /** Experience needed for next level */
  public experienceToNextLevel: number;

  /** Element types (up to 2) */
  public elementTypes: ElementType[];

  /** Equipped ability IDs (max 4 active) */
  public equippedAbilities: string[];

  /** Evolution stage (0 = base form) */
  public evolutionStage: number;

  /** Loyalty/bond with player (0-100) */
  public loyalty: number;

  /** Personality affecting behavior */
  public personality: PersonalityType;

  /** Owner's player ID (for multiplayer) */
  public ownerId: string;

  /** Whether this is a wild (untamed) beastie */
  public isWild: boolean;

  /** Base stats (from species + IVs) */
  public baseStats: BeastieStats;

  /** Individual values (genetic variation) */
  public ivs: BeastieStats;

  /** Effort values (training bonuses) */
  public evs: BeastieStats;

  constructor(data?: Partial<BeastieComponent>) {
    super();
    this.speciesId = data?.speciesId ?? '';
    this.nickname = data?.nickname ?? '';
    this.level = data?.level ?? 1;
    this.experience = data?.experience ?? 0;
    this.experienceToNextLevel = data?.experienceToNextLevel ?? 100;
    this.elementTypes = data?.elementTypes ?? [];
    this.equippedAbilities = data?.equippedAbilities ?? [];
    this.evolutionStage = data?.evolutionStage ?? 0;
    this.loyalty = data?.loyalty ?? 50;
    this.personality = data?.personality ?? 'bold';
    this.ownerId = data?.ownerId ?? '';
    this.isWild = data?.isWild ?? true;
    this.baseStats = data?.baseStats ?? createDefaultStats();
    this.ivs = data?.ivs ?? createRandomIVs();
    this.evs = data?.evs ?? createDefaultStats();
  }

  /**
   * Get calculated stat value
   */
  public getStat(stat: keyof BeastieStats): number {
    const base = this.baseStats[stat];
    const iv = this.ivs[stat];
    const ev = this.evs[stat];

    // Standard stat formula: ((2*base + iv + ev/4) * level / 100) + 5
    return Math.floor(((2 * base + iv + ev / 4) * this.level) / 100) + 5;
  }

  /**
   * Add experience and check for level up
   */
  public addExperience(amount: number): boolean {
    this.experience += amount;
    if (this.experience >= this.experienceToNextLevel) {
      return this.levelUp();
    }
    return false;
  }

  /**
   * Level up the beastie
   */
  private levelUp(): boolean {
    this.level++;
    this.experience -= this.experienceToNextLevel;
    // Increase experience needed (simple curve)
    this.experienceToNextLevel = Math.floor(this.experienceToNextLevel * 1.2);
    return true;
  }
}

/**
 * Beastie stats structure
 */
export interface BeastieStats {
  health: number;
  attack: number;
  defense: number;
  speed: number;
  energy: number;
}

/**
 * Create default (zero) stats
 */
function createDefaultStats(): BeastieStats {
  return {
    health: 0,
    attack: 0,
    defense: 0,
    speed: 0,
    energy: 0,
  };
}

/**
 * Create random IVs (0-15 for each stat)
 */
function createRandomIVs(): BeastieStats {
  return {
    health: Math.floor(Math.random() * 16),
    attack: Math.floor(Math.random() * 16),
    defense: Math.floor(Math.random() * 16),
    speed: Math.floor(Math.random() * 16),
    energy: Math.floor(Math.random() * 16),
  };
}
